<template lang="pug">

v-container(fluid)
    v-card(class="optimization-parameters-card pa-0 mb-4 mt-1" light)
        .optimization-header.pa-3.d-flex.align-center
            v-icon.mr-2(color="primary" size="28") tune
            span.text-h6.font-weight-bold Optimization Overview
        v-divider
        v-list(dense class="pa-2")
            v-list-item
                v-list-item-icon
                    v-icon(:color="optimizationStats?.foundFeasible ? 'success' : 'error'") check_circle
                v-list-item-content
                    v-list-item-title Found feasible solution
            v-list-item
                v-list-item-content
                    v-list-item-title Total Attempts: {{ optimizationStats?.totalAttempts }}
            v-list-item
                v-list-item-content
                    v-simple-table(class="params-table")
                        thead
                            tr
                                th Parameter
                                th Config
                                th Used
                                th Info
                        tbody
                            tr
                                th Production Max
                                    sup {{optimizationOriginalParameters.prodUom}}
                                td {{formatValueWithoutUnit(optimizationOriginalParameters.productionMax, productionUnit) }}
                                td {{formatValueWithoutUnit(optimizationParameters.productionMax, productionUnit) }}
                                td
                                    v-tooltip(bottom :max-width="320" :color="'grey darken-4'" transition="scale-transition")
                                        template(v-slot:activator="{ on, attrs }")
                                            span(v-bind="attrs" v-on="on" class="tooltip-activator") Atts: {{optimizationStats.productionMinAttempt }}
                                        div(class="custom-tooltip")
                                            span Number of attempts to minimize production below the configured maximum.
                                                |  A higher number indicates more effort was needed to find a solution.

                            tr
                                th Storage Max
                                    sup {{storageUnitShort}}
                                td {{formatValueWithoutUnit(optimizationOriginalParameters.storageMax, storageUnitShort) }}
                                td {{formatValueWithoutUnit(optimizationParameters.storageMax, storageUnitShort) }}
                                td
                                    v-tooltip(bottom :max-width="320" :color="'grey darken-4'" transition="scale-transition")
                                        template(v-slot:activator="{ on, attrs }")
                                            span(v-bind="attrs" v-on="on" class="tooltip-activator") HB: {{ optimizationStats.hitBounds.storageMaxLower }}
                                        div(class="custom-tooltip")
                                            span When true, storage is capped at the configured maximum.
                                                |  The optimizer did not reduce below this cap.
                                                |  To allow a lower value, adjust configuration.
                            tr
                                th Storage Min
                                    sup {{storageUnitShort}}
                                td {{formatValueWithoutUnit(optimizationOriginalParameters.storageMin, storageUnitShort) }}
                                td {{formatValueWithoutUnit(optimizationParameters.storageMin, storageUnitShort) }}
                                td
                                    v-tooltip(bottom :max-width="320" :color="'grey darken-4'" transition="scale-transition")
                                        template(v-slot:activator="{ on, attrs }")
                                            span(v-bind="attrs" v-on="on" class="tooltip-activator") HB: {{ optimizationStats.hitBounds.storageMinLower }}
                                        div(class="custom-tooltip")
                                            span When true: storage reserve held at the configured minimum.
                                                |  It cannot go lower without a configuration change.
                                    v-tooltip(v-if="hasStorageMinSuggestion" bottom :max-width="320" :color="'grey darken-4'" transition="scale-transition")
                                        template(v-slot:activator="{ on, attrs }")
                                            v-icon(v-bind="attrs" v-on="on" color="warning" small class="ml-1") lightbulb
                                        div(class="custom-tooltip")
                                            span Lowering storage minimum to {{ formatValueWithUnit(optimizationStats.suggestedStorageMin, storageUnitShort) }} could save
                                                |  {{ formatValueWithUnit(optimizationStats.storageMinSavings, storageUnitShort) }} of production volume over the optimization horizon (5 days).
            v-list-item(v-if="hasStorageMinSuggestion" class="px-2 pt-1")
                v-alert(type="info" dense text border="left" class="mb-0 w-100" color="orange darken-2")
                    span.font-weight-bold Optimization Tip:&nbsp;
                    span Lowering Storage Min from
                        |  {{ formatValueWithoutUnit(optimizationOriginalParameters.storageMin, storageUnitShort) }} to {{ formatValueWithoutUnit(optimizationStats.suggestedStorageMin, storageUnitShort) }} {{ storageUnitShort }}
                        |  could save {{ formatValueWithUnit(optimizationStats.storageMinSavings, storageUnitShort) }} of production volume over the optimization horizon (5 days).
            v-list-item(v-if="hasDailyStorageMetrics")
                v-list-item-content
                    .section-label Day 1 Storage Behavior
                    v-simple-table(class="params-table mt-1")
                        tbody
                            tr
                                th Peak Storage
                                td {{ formatValueWithUnit(dailyStorageMetrics.peakStorage, storageUnitShort) }}
                            tr
                                th Lowest Storage
                                td {{ formatValueWithUnit(dailyStorageMetrics.lowestStorage, storageUnitShort) }}
                            tr
                                th Hours At Storage Max
                                td {{ dailyStorageMetrics.hoursAtMax }}
                            tr
                                th Hours At Storage Min
                                td {{ dailyStorageMetrics.hoursAtMin }}
                            tr
                                th End-of-Day Storage Change
                                td(:class="dailyStorageMetrics.endOfDayDelta < 0 ? 'metric-negative' : 'metric-positive'") {{ formatSignedValueWithUnit(dailyStorageMetrics.endOfDayDelta, storageUnitShort) }}
            v-list-item(v-if="hasHistoricalStats")
                v-list-item-content
                    .section-label Historical Stats (Last {{ historicalStats.daysUsed }} Days, Hourly)
                    .section-subtitle Zero values excluded
                    v-simple-table(class="params-table historical-stats-table mt-1" dense)
                        thead
                            tr
                                th Metric
                                th
                                    div Demand
                                    div(class="historical-uom") {{ productionUnit }}
                                th
                                    div Production
                                    div(class="historical-uom") {{ productionUnit }}
                                th
                                    div Storage
                                    div(class="historical-uom") {{ storageUnitShort }}
                        tbody
                            tr
                                th Median
                                td {{ formatHistoricalStatValue(historicalStats.demand.median, productionUnit) }}
                                td {{ formatHistoricalStatValue(historicalStats.production.median, productionUnit) }}
                                td {{ formatHistoricalStatValue(historicalStats.storage.median, storageUnitShort) }}
                            tr
                                th Mean
                                td {{ formatHistoricalStatValue(historicalStats.demand.mean, productionUnit) }}
                                td {{ formatHistoricalStatValue(historicalStats.production.mean, productionUnit) }}
                                td {{ formatHistoricalStatValue(historicalStats.storage.mean, storageUnitShort) }}
                            tr
                                th Max
                                td {{ formatHistoricalStatValue(historicalStats.demand.max, productionUnit) }}
                                td {{ formatHistoricalStatValue(historicalStats.production.max, productionUnit) }}
                                td {{ formatHistoricalStatValue(historicalStats.storage.max, storageUnitShort) }}
                            tr
                                th Min
                                td {{ formatHistoricalMinValue(historicalStats.demand.min, productionUnit) }}
                                td {{ formatHistoricalMinValue(historicalStats.production.min, productionUnit) }}
                                td {{ formatHistoricalMinValue(historicalStats.storage.min, storageUnitShort) }}
                            tr.quartile-group-header(@click="showQ1Outliers = !showQ1Outliers")
                                th
                                    v-tooltip(bottom :max-width="320" :color="'grey darken-4'" transition="scale-transition")
                                        template(v-slot:activator="{ on, attrs }")
                                            span(v-bind="attrs" v-on="on" class="tooltip-activator") Quartile (Q1)
                                        div(class="custom-tooltip")
                                            span About 25% of historical hourly values are at or below this value.
                                    v-icon(small class="ml-1 quartile-chevron" :class="{ 'rotated': showQ1Outliers }") expand_more
                                td {{ formatHistoricalStatValue(historicalStats.demand.q1, productionUnit) }}
                                td {{ formatHistoricalStatValue(historicalStats.production.q1, productionUnit) }}
                                td {{ formatHistoricalStatValue(historicalStats.storage.q1, storageUnitShort) }}
                            tr(v-show="showQ1Outliers" class="quartile-outlier-row")
                                th
                                    v-tooltip(bottom :max-width="320" :color="'grey darken-4'" transition="scale-transition")
                                        template(v-slot:activator="{ on, attrs }")
                                            span(v-bind="attrs" v-on="on" class="tooltip-activator") Outliers (Lower)
                                                sup &nbsp;iqr
                                        div(class="custom-tooltip")
                                            span Count of lower outliers using Tukey fences: values below Q1 - (1.5 × IQR),
                                                |  where IQR = Q3 - Q1.
                                td {{ formatCountValue(historicalStats.demand.outlierLowerCount) }}
                                td {{ formatCountValue(historicalStats.production.outlierLowerCount) }}
                                td {{ formatCountValue(historicalStats.storage.outlierLowerCount) }}
                            tr(v-show="showQ1Outliers" class="quartile-outlier-row")
                                th
                                    v-tooltip(bottom :max-width="320" :color="'grey darken-4'" transition="scale-transition")
                                        template(v-slot:activator="{ on, attrs }")
                                            span(v-bind="attrs" v-on="on" class="tooltip-activator") Outlier % (Lower)
                                                sup &nbsp;iqr
                                        div(class="custom-tooltip")
                                            span Percentage of non-zero values below the Tukey lower fence:
                                                |  Q1 - (1.5 × IQR), where IQR = Q3 - Q1.
                                                |  Computed as (Outliers Lower / Count) × 100.
                                td {{ formatPercentValue(historicalStats.demand.outlierLowerPercent) }}
                                td {{ formatPercentValue(historicalStats.production.outlierLowerPercent) }}
                                td {{ formatPercentValue(historicalStats.storage.outlierLowerPercent) }}
                            tr(v-show="showQ1Outliers" class="quartile-outlier-row")
                                th
                                    v-tooltip(bottom :max-width="320" :color="'grey darken-4'" transition="scale-transition")
                                        template(v-slot:activator="{ on, attrs }")
                                            span(v-bind="attrs" v-on="on" class="tooltip-activator") Low-tail cutoff (P5)
                                        div(class="custom-tooltip")
                                            span 5th percentile cutoff for non-zero historical values.
                                                |  Values at or below this threshold are marked as low-tail anomalies.
                                td {{ formatHistoricalStatValue(historicalStats.demand.lowTailAnomalyCutoff, productionUnit) }}
                                td {{ formatHistoricalStatValue(historicalStats.production.lowTailAnomalyCutoff, productionUnit) }}
                                td {{ formatHistoricalStatValue(historicalStats.storage.lowTailAnomalyCutoff, storageUnitShort) }}
                            tr(v-show="showQ1Outliers" class="quartile-outlier-row")
                                th
                                    v-tooltip(bottom :max-width="320" :color="'grey darken-4'" transition="scale-transition")
                                        template(v-slot:activator="{ on, attrs }")
                                            span(v-bind="attrs" v-on="on" class="tooltip-activator") Low-tail anomalies
                                        div(class="custom-tooltip")
                                            span Count of non-zero historical values at or below the P5 cutoff.
                                td {{ formatCountValue(historicalStats.demand.lowTailAnomalyCount) }}
                                td {{ formatCountValue(historicalStats.production.lowTailAnomalyCount) }}
                                td {{ formatCountValue(historicalStats.storage.lowTailAnomalyCount) }}
                            tr(v-show="showQ1Outliers" class="quartile-outlier-row")
                                th
                                    v-tooltip(bottom :max-width="320" :color="'grey darken-4'" transition="scale-transition")
                                        template(v-slot:activator="{ on, attrs }")
                                            span(v-bind="attrs" v-on="on" class="tooltip-activator") Low-tail anomaly %
                                        div(class="custom-tooltip")
                                            span Percentage of non-zero historical values at or below the P5 cutoff.
                                td {{ formatPercentValue(historicalStats.demand.lowTailAnomalyPercent) }}
                                td {{ formatPercentValue(historicalStats.production.lowTailAnomalyPercent) }}
                                td {{ formatPercentValue(historicalStats.storage.lowTailAnomalyPercent) }}
                            tr.quartile-group-header(@click="showQ3Outliers = !showQ3Outliers")
                                th
                                    v-tooltip(bottom :max-width="320" :color="'grey darken-4'" transition="scale-transition")
                                        template(v-slot:activator="{ on, attrs }")
                                            span(v-bind="attrs" v-on="on" class="tooltip-activator") Quartile (Q3)
                                        div(class="custom-tooltip")
                                            span About 75% of historical hourly values are at or below this value.
                                    v-icon(small class="ml-1 quartile-chevron" :class="{ 'rotated': showQ3Outliers }") expand_more
                                td {{ formatHistoricalStatValue(historicalStats.demand.q3, productionUnit) }}
                                td {{ formatHistoricalStatValue(historicalStats.production.q3, productionUnit) }}
                                td {{ formatHistoricalStatValue(historicalStats.storage.q3, storageUnitShort) }}
                            tr(v-show="showQ3Outliers" class="quartile-outlier-row")
                                th
                                    v-tooltip(bottom :max-width="320" :color="'grey darken-4'" transition="scale-transition")
                                        template(v-slot:activator="{ on, attrs }")
                                            span(v-bind="attrs" v-on="on" class="tooltip-activator") Outliers (Upper)
                                                sup &nbsp;iqr
                                        div(class="custom-tooltip")
                                            span Count of upper outliers using Tukey fences: values above Q3 + (1.5 × IQR),
                                                |  where IQR = Q3 - Q1.
                                                |  They can skew the mean and affect forecast accuracy.
                                                |  A high outlier count may indicate data quality issues or unusual events.
                                td {{ formatCountValue(historicalStats.demand.outlierUpperCount) }}
                                td {{ formatCountValue(historicalStats.production.outlierUpperCount) }}
                                td {{ formatCountValue(historicalStats.storage.outlierUpperCount) }}
                            tr(v-show="showQ3Outliers" class="quartile-outlier-row")
                                th
                                    v-tooltip(bottom :max-width="320" :color="'grey darken-4'" transition="scale-transition")
                                        template(v-slot:activator="{ on, attrs }")
                                            span(v-bind="attrs" v-on="on" class="tooltip-activator") Outlier % (Upper)
                                                sup &nbsp;iqr
                                        div(class="custom-tooltip")
                                            span Percentage of non-zero values above the Tukey upper fence:
                                                |  Q3 + (1.5 × IQR), where IQR = Q3 - Q1.
                                                |  Computed as (Outliers Upper / Count) × 100.
                                td {{ formatPercentValue(historicalStats.demand.outlierUpperPercent) }}
                                td {{ formatPercentValue(historicalStats.production.outlierUpperPercent) }}
                                td {{ formatPercentValue(historicalStats.storage.outlierUpperPercent) }}
                            tr(v-if="showZeroCount" class="zeros-row")
                                th
                                    v-tooltip(bottom :max-width="340" :color="'grey darken-4'" transition="scale-transition")
                                        template(v-slot:activator="{ on, attrs }")
                                            span(v-bind="attrs" v-on="on" class="tooltip-activator") Zeros
                                        div(class="custom-tooltip")
                                            span Zero values in historical data reduce forecast accuracy.
                                                |  A forecasting model learns patterns from non-zero readings.
                                                |  When a large share of hourly buckets are zero, the model cannot
                                                |  distinguish normal low-demand periods from outages or missing data,
                                                |  leading to under-predicted demand and unreliable optimization targets.
                                td {{ formatCountValue(historicalStats.demand.zeroCount) }}
                                td {{ formatCountValue(historicalStats.production.zeroCount) }}
                                td {{ formatCountValue(historicalStats.storage.zeroCount) }}
</template>

<script lang="js">

export default {
    name: 'OptimizationInfo',
    props: {
        optimizationOriginalParameters: {
            type: Object,
            required: true
        },
        optimizationParameters: {
            type: Object,
            required: true
        },
        optimizationStats: {
            type: Object,
            required: true
        },
        latestSeriesData: {
            type: Array,
            default: () => []
        }
    },
    data () {
        return {
            showQ1Outliers: false,
            showQ3Outliers: false,
        };
    },
    computed: {
        hasStorageMinSuggestion () {
            return Number.isFinite(this.optimizationStats?.suggestedStorageMin)
                && Number(this.optimizationStats?.storageMinSavings) > 0;
        },
        storageUnitShort () {
            const storageUnit = this.optimizationOriginalParameters?.storageUom.toLowerCase() || '';
            if (storageUnit === 'gal' || storageUnit === 'gallon' || storageUnit === 'gallons') {
                return 'gal';
            }

            return storageUnit || '';
        },
        productionUnit () {
            const prodUnit = this.optimizationOriginalParameters?.prodUom.toLowerCase() || '';
            return prodUnit || '';
        },
        storageSeries () {
            const storageSeries = (this.latestSeriesData || []).find((series) => series?.name === 'storage');
            return Array.isArray(storageSeries?.data)
                ? storageSeries.data.filter((value) => Number.isFinite(Number(value))).map(Number)
                : [];
        },
        hasDailyStorageMetrics () {
            return this.storageSeries.length > 0;
        },
        dailyStorageMetrics () {
            if (!this.hasDailyStorageMetrics) {
                return null;
            }

            const usedStorageMax = Number(this.optimizationParameters?.storageMax);
            const usedStorageMin = Number(this.optimizationParameters?.storageMin);
            const peakStorage = Math.max(...this.storageSeries);
            const lowestStorage = Math.min(...this.storageSeries);
            const firstStorage = this.storageSeries[0];
            const lastStorage = this.storageSeries[this.storageSeries.length - 1];
            const endOfDayDelta = lastStorage - firstStorage;

            return {
                peakStorage,
                lowestStorage,
                hoursAtMax: Number.isFinite(usedStorageMax)
                    ? this.storageSeries.filter((value) => this.isNearBound(value, usedStorageMax)).length
                    : 0,
                hoursAtMin: Number.isFinite(usedStorageMin)
                    ? this.storageSeries.filter((value) => this.isNearBound(value, usedStorageMin)).length
                    : 0,
                endOfDayDelta,
            };
        },
        historicalStats () {
            return this.optimizationStats?.historicalStats || null;
        },
        hasHistoricalStats () {
            const hasStatsForSeries = (seriesStats) => {
                if (!seriesStats || typeof seriesStats !== 'object') {
                    return false;
                }

                return ['max', 'min', 'median', 'mean'].every((metric) => Number.isFinite(Number(seriesStats[metric])));
            };

            return hasStatsForSeries(this.historicalStats?.demand)
                && hasStatsForSeries(this.historicalStats?.production)
                && hasStatsForSeries(this.historicalStats?.storage);
        },

        showZeroCount () {
            return this.historicalStats?.demand?.zeroCount > 0
                || this.historicalStats?.production?.zeroCount > 0
                || this.historicalStats?.storage?.zeroCount > 0;
        }
    },
    methods: {
        adjustValueForUnit (value, unit) {
            const numericValue = Number(value);
            if (!Number.isFinite(numericValue)) {
                return '0';
            }

            const normalizedUnit = (unit || '').trim().toLowerCase();
            switch (normalizedUnit) {
            case 'gal':
            case 'gpm':
            case 'gallons':
                return Number(Math.round(numericValue)).toLocaleString('en-US');
            case 'gph':
                return Number(numericValue.toFixed(1)).toLocaleString('en-US');
            case 'gpd':
                return Number(numericValue.toFixed(2)).toLocaleString('en-US');
            default:
                return Number(numericValue.toFixed(3)).toLocaleString('en-US');
            }
        },

        formatValueWithUnit (value, unit) {
            const numericValue = Number(value);
            if (!Number.isFinite(numericValue)) {
                return `0 ${unit || ''}`.trim();
            }

            const clamped = Math.max(0, numericValue);
            const formatted = this.adjustValueForUnit(clamped, unit);
            return `${formatted} ${unit || ''}`.trim();
        },
        formatValueWithoutUnit (value, unit) {
            const numericValue = Number(value);
            if (!Number.isFinite(numericValue)) {
                return '0';
            }

            const clamped = Math.max(0, numericValue);
            const formatted = this.adjustValueForUnit(clamped, unit);
            return `${formatted}`;
        },
        formatHistoricalStatValue (value, unit) {
            const numericValue = Number(value);
            if (!Number.isFinite(numericValue)) {
                return '-';
            }

            return this.adjustValueForUnit(numericValue, unit);
        },
        formatSmallNonZeroValue (value) {
            const numericValue = Number(value);
            if (!Number.isFinite(numericValue)) {
                return '-';
            }

            const absValue = Math.abs(numericValue);
            for (let decimals = 1; decimals <= 8; decimals += 1) {
                if (Math.round(absValue * (10 ** decimals)) > 0) {
                    return numericValue.toLocaleString('en-US', {
                        minimumFractionDigits: decimals,
                        maximumFractionDigits: decimals,
                    });
                }
            }

            return numericValue.toExponential(2);
        },
        formatHistoricalMinValue (value, unit) {
            const numericValue = Number(value);
            if (!Number.isFinite(numericValue)) {
                return '-';
            }

            const defaultFormattedValue = this.adjustValueForUnit(numericValue, unit);
            const parsedDefaultValue = Number(String(defaultFormattedValue).replaceAll(',', ''));

            if (numericValue !== 0 && parsedDefaultValue === 0) {
                return this.formatSmallNonZeroValue(numericValue);
            }

            return defaultFormattedValue;
        },
        formatCountValue (value) {
            const numericValue = Number(value);
            if (!Number.isFinite(numericValue)) {
                return '-';
            }

            return Math.round(numericValue).toLocaleString('en-US');
        },
        formatPercentValue (value) {
            const numericValue = Number(value);
            if (!Number.isFinite(numericValue)) {
                return '-';
            }

            return `${numericValue.toFixed(2)}%`;
        },
        formatSignedValueWithUnit (value, unit) {
            const numericValue = Number(value);
            if (!Number.isFinite(numericValue)) {
                return `0 ${unit}`;
            }

            const sign = numericValue > 0 ? '+' : '';
            const formatted = this.adjustValueForUnit(numericValue, unit);
            return `${sign}${formatted} ${unit}`;
        },
        formatHistoricalValue (value) {
            const numericValue = Number(value);
            if (!Number.isFinite(numericValue)) {
                return '-';
            }

            return this.adjustValueForUnit(numericValue);
        },
        isNearBound (value, bound) {
            const tolerance = Math.max(0.001, Math.abs(bound) * 0.000001);
            return Math.abs(Number(value) - Number(bound)) <= tolerance;
        }
    }
};


</script>

<style lang="css" scoped>

    .optimization-parameters-card {
        background: linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 100%);
        border: 1px solid #1976D2;
        color: #115293;
        border-radius: 14px;
        box-shadow: 10px 4px 16px rgba(25, 118, 210, 0.10);
        overflow: hidden;
        /* max-height: 420px; Adjust as needed to match FlowChart height */
        display: flex;
        flex-direction: column;
    }
    .optimization-header {
        background-color: #00355c;
        color: #fff;
        border-top-left-radius: 14px;
        border-top-right-radius: 14px;
        font-size: 1.15em;
        letter-spacing: 0.5px;
    }
    .optimization-parameters-card .v-list {
        overflow-y: auto;
        flex: 1 1 auto;
        max-height: 325px; /* Ensures list scrolls if content overflows */
    }
    .optimization-parameters-card .v-list-item {
        padding: 2px 0 2px 8px;
        border-radius: 6px;
        margin-bottom: 0;
    }
    .optimization-parameters-card .v-list-item-title {
        font-weight: 600;
        color: #00355c;
        font-size: 1em;
    }
    .optimization-parameters-card .v-list-item-subtitle {
        color: #333;
        font-size: 0.95em;
    }
    .optimization-parameters-card .v-list-item-icon {
        margin-right: 10px;
    }

    /* Ensure v-simple-table header is bold (override Vuetify) */
    .params-table >>> .v-data-table__wrapper > table > thead > tr > th {
        font-weight: 800 !important;
        color: #00355C !important;
    }
    /* Ensure v-simple-table header is bold (override Vuetify) */
    .params-table >>> .v-data-table__wrapper > table > tbody > tr > th {
        font-weight: 800 !important;
        color: #0a293f !important;
    }
    .custom-tooltip {
        padding: 12px;
        color: #fff;
        background: #222;
        border-radius: 8px;
        max-width: 320px;
        white-space: normal;
        position: relative;
        font-size: 1em;
        line-height: 1.4;
        box-shadow: 0 2px 8px rgba(0,0,0,0.18);
    }
    .tooltip-activator {
        cursor: help;
        text-decoration: underline;
        color: #1976D2;
    }
    .metric-negative {
        color: #b71c1c;
        font-weight: 700;
    }
    .metric-positive {
        color: #1b5e20;
        font-weight: 700;
    }
    .section-label {
        font-weight: 700;
        font-size: 0.8em;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: #fff;
        background-color: #00355c;
        border-radius: 4px;
        padding: 2px 8px;
        margin-bottom: 4px;
        display: inline-block;
    }
    .section-subtitle {
        font-weight: 700;
        font-size: 0.78em;
        letter-spacing: 0.04em;
        color: #00355c;
        margin-bottom: 4px;
        background: none;
    }
    .historical-uom {
        font-size: 0.8em;
        font-weight: 600;
        color: #115293;
        line-height: 1.1;
    }
    .quartile-group-header {
        cursor: pointer;
        user-select: none;
    }
    .quartile-group-header:hover td,
    .quartile-group-header:hover th {
        background-color: rgba(25, 118, 210, 0.06) !important;
    }
    .quartile-chevron {
        vertical-align: middle;
        transition: transform 0.2s ease;
        color: #1976D2 !important;
    }
    .quartile-chevron.rotated {
        transform: rotate(180deg);
    }
    .quartile-outlier-row th,
    .quartile-outlier-row td {
        color: #555 !important;
        font-style: italic;
    }
    .zeros-row th,
    .zeros-row td {
        border-top: 2px solid #b0bec5 !important;
    }
</style>
